import { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableContainer
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { TableCard } from '@/components/shared/Card';
import CardHeader from '@/components/shared/CardHeader';
import Chip from '@/components/shared/Chip';
import { Dropdown, DropdownType } from '@/components/shared/Dropdown';
import { TablePagination } from '@/components/shared/TablePagination';
import {
    TableCell,
    TableCellFirstCell,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellFirstCell,
    TableRow
} from '@/components/shared/styles/Table';
import { SMBold, SMRegular, XSMedium } from '@/typography/Typography';
import { useUser } from '@/contexts/user/UserContext';
import { UserRoles } from '@/models/company';
import { DATE_FORMAT } from '@/lib/constants';
import { useUserEventsQuery } from '@/services/reportsQuery';
import { UserCommunicationTimelineEntry } from '@/services/UserCommunicationHistoryTypes';
import {
    USER_EVENTS_MAX_PAGE_SIZE,
    USER_EVENTS_PAGE_SIZE
} from '@/services/UserEventsTypes';

interface UserEventsTableProps {
    userId: string;
}

const COMMUNICATION_KIND: UserCommunicationTimelineEntry['kind'] =
    'Communication';

const ROWS_PER_PAGE_OPTIONS = [
    { label: `${USER_EVENTS_PAGE_SIZE}`, value: USER_EVENTS_PAGE_SIZE },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
    {
        label: `${USER_EVENTS_MAX_PAGE_SIZE}`,
        value: USER_EVENTS_MAX_PAGE_SIZE
    }
];

export const UserEventsTable = ({ userId }: UserEventsTableProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, company } = useUser();
    const { pageNumber } = useParams();

    // The page only mounts this tab for a SuperAdmin; re-checking here keeps a direct hit on the
    // tab's route from firing a request the backend would 403 anyway.
    const isSuperAdmin = user?.role === UserRoles.SuperAdmin;
    const companyId = company?.companyId ?? '';

    const [rowsPerPage, setRowsPerPage] = useState(USER_EVENTS_PAGE_SIZE);
    const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);

    // TablePagination navigates rather than lifting state, so the route segment is the only source
    // of truth for the page. Deriving it here keeps the two from drifting apart.
    const parsedPage = parseInt(pageNumber ?? '');
    const page = Number.isNaN(parsedPage) || parsedPage < 0 ? 0 : parsedPage;

    const { data, isFetching, isError } = useUserEventsQuery(
        companyId,
        userId,
        {
            eventTypes: selectedEventTypes,
            limit: rowsPerPage,
            offset: page * rowsPerPage
        },
        isSuperAdmin && !!companyId && !!userId
    );

    const events = data?.events ?? [];
    const eventTypeCounts = data?.eventTypeCounts ?? [];

    // Choices come from counts over the whole feed rather than the loaded page, so the list neither
    // shrinks as an operator pages nor collapses to whatever the current filter already allows.
    const eventTypeChoices = useMemo(
        () =>
            eventTypeCounts.map(({ eventType, count }) => ({
                label: `${eventType} (${count})`,
                value: eventType
            })),
        [eventTypeCounts]
    );

    /** Any narrowing invalidates the current page number, so send the operator back to page 0. */
    const resetToFirstPage = () =>
        navigate('../0', { replace: true, relative: 'path' });

    const handleEventTypesChange = (value: string[] | null) => {
        setSelectedEventTypes(value ?? []);
        resetToFirstPage();
    };

    if (!isSuperAdmin) return null;

    return (
        <TableCard>
            <CardHeader
                label={data?.displayIdentity ?? t('user_events.title')}
                description={t('user_events.subtitle')}
                chip={
                    data?.isDeletedUser ? (
                        <Chip
                            color="muted"
                            icon={<span></span>}
                            label={
                                <XSMedium>
                                    {t('user_events.deleted_user')}
                                </XSMedium>
                            }
                        />
                    ) : undefined
                }
                actions={
                    eventTypeChoices.length > 0 ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <SMBold noWrap>{t('user_events.filter_by')}</SMBold>
                            <Box minWidth="200px">
                                <Dropdown
                                    label={t('user_events.all_events')}
                                    choices={eventTypeChoices}
                                    type={DropdownType.Multi}
                                    value={selectedEventTypes}
                                    onChange={handleEventTypesChange}
                                />
                            </Box>
                        </Box>
                    ) : undefined
                }
            />

            {isFetching && (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress size={32} />
                </Box>
            )}

            {!isFetching && isError && (
                <Box px={2} pb={2}>
                    <Alert severity="error">{t('user_events.error')}</Alert>
                </Box>
            )}

            {!isFetching && !isError && events.length === 0 && (
                <Box px={2} py={4}>
                    <SMRegular color="text.secondary">
                        {selectedEventTypes.length > 0
                            ? t('user_events.no_events_for_filter')
                            : t('user_events.no_events')}
                    </SMRegular>
                </Box>
            )}

            {!isFetching && !isError && events.length > 0 && (
                <>
                    <TableContainer>
                        <Table aria-label="user events table">
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCellFirstCell>
                                        {t('user_events.col_when')}
                                    </TableHeaderCellFirstCell>
                                    <TableHeaderCell>
                                        {t('user_events.col_type')}
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        {t('user_events.col_channel')}
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        {t('user_events.col_event')}
                                    </TableHeaderCell>
                                    <TableHeaderCell>
                                        {t('user_events.col_detail')}
                                    </TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {events.map((event, index) => (
                                    <EventRow
                                        key={`${event.occurredAt}-${event.eventType}-${index}`}
                                        event={event}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        page={page}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        count={data?.total ?? 0}
                        rowsPerPage={rowsPerPage}
                        resetPage={resetToFirstPage}
                        onPageChange={() => {}}
                        // TablePagination calls resetPage itself before this, so the page number is
                        // already back to 0 by the time the new size lands.
                        onRowsPerPageChange={(newRowsPerPage) =>
                            setRowsPerPage(newRowsPerPage as number)
                        }
                    />
                </>
            )}
        </TableCard>
    );
};

interface EventRowProps {
    event: UserCommunicationTimelineEntry;
}

const EventRow = ({ event }: EventRowProps) => {
    const { t } = useTranslation();
    const isCommunication = event.kind === COMMUNICATION_KIND;

    const detail =
        event.campaignName || event.templateId || event.clickedUrl || '—';

    return (
        <TableRow>
            <TableCellFirstCell style={{ width: '240px' }}>
                <SMRegular>
                    {dayjs(event.occurredAt).format(DATE_FORMAT.FULL_DATE_TIME)}
                </SMRegular>
            </TableCellFirstCell>
            <TableCell style={{ width: '160px' }}>
                <Chip
                    color={isCommunication ? 'info' : 'success'}
                    label={
                        <XSMedium>
                            {isCommunication
                                ? t('user_events.kind_communication')
                                : t('user_events.kind_activity')}
                        </XSMedium>
                    }
                />
            </TableCell>
            <TableCell style={{ width: '120px' }}>
                <SMRegular>{event.channel ?? '—'}</SMRegular>
            </TableCell>
            <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                    <SMRegular>{event.eventType}</SMRegular>
                    {event.collapsedCount > 0 && (
                        <Chip
                            color="muted"
                            icon={<span></span>}
                            label={
                                <XSMedium>
                                    {t('user_events.collapsed_count', {
                                        count: event.collapsedCount
                                    })}
                                </XSMedium>
                            }
                        />
                    )}
                </Box>
            </TableCell>
            <TableCell>
                <SMRegular>{detail}</SMRegular>
            </TableCell>
        </TableRow>
    );
};
